// 潮汐ステータスカード（v1.1）
// レイアウトを常時固定。当日と当日以外で内容を切り替えるが高さは同じに保つ。

import { useMemo } from 'react';
import type { TideResponse } from '@tideprecision/shared';
import { computeTideMotion } from '../lib/tideMotion.js';
import { formatWindDirection, windSpeedTextClass } from '../lib/wind.js';
import type { WindForecastHour, WindPeriodSummary } from '../lib/windTypes.js';

interface Props {
  data: TideResponse;
  /** 当日の場合は現在の分オフセット (0..1439)、当日以外は null */
  currentMinute: number | null;
  currentWind: WindForecastHour | null;
  windSummary: WindPeriodSummary[];
}

const MOTION_STYLE: Record<string, string> = {
  rising: 'text-emerald-300',
  falling: 'text-amber-300',
};

export function TideStatusCard({
  data,
  currentMinute,
  currentWind,
  windSummary,
}: Props): JSX.Element {
  const isToday = currentMinute !== null;

  const todayInfo = useMemo(() => {
    if (!isToday) return null;
    const level = data.levels[Math.min(currentMinute!, data.levels.length - 1)] ?? 0;
    const motion = computeTideMotion(data);
    return { level, motion };
  }, [data, currentMinute, isToday]);

  const summary = useMemo(() => {
    if (data.levels.length === 0) return null;
    let max = data.levels[0]!;
    let min = data.levels[0]!;
    let maxIdx = 0;
    let minIdx = 0;
    for (let i = 1; i < data.levels.length; i++) {
      const v = data.levels[i]!;
      if (v > max) {
        max = v;
        maxIdx = i;
      }
      if (v < min) {
        min = v;
        minIdx = i;
      }
    }
    return { max, min, maxIdx, minIdx, range: max - min };
  }, [data.levels]);

  // 高さ固定：当日カードも当日以外も同じグリッド構造
  return (
    <div className="rounded bg-slate-800/60 border border-slate-700/40 p-4 min-h-[216px] flex flex-col justify-center">
      {isToday && todayInfo ? (
        <div className="text-center space-y-2">
          <div className="text-xs text-slate-400">現在潮位</div>
          <div className="text-tide-now text-sky-300 font-mono leading-none">
            {todayInfo.level.toFixed(2)}
            <span className="text-2xl ml-1 text-slate-400">m</span>
          </div>
          {todayInfo.motion && (
            <div
              className={`text-base font-bold ${MOTION_STYLE[todayInfo.motion.kind] ?? 'text-slate-200'}`}
            >
              <span className="text-2xl mr-1">{todayInfo.motion.arrow}</span>
              {todayInfo.motion.label}
            </div>
          )}
          {currentWind && <CurrentWind wind={currentWind} />}
        </div>
      ) : (
        summary && (
          <div className="text-center space-y-1">
            <div className="text-xs text-slate-400">{data.date} の潮汐サマリ</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div>
                <div className="text-xs text-rose-300">最高潮位</div>
                <div className="text-2xl font-mono text-rose-200">
                  {summary.max.toFixed(2)}
                  <span className="text-sm text-slate-400 ml-0.5">m</span>
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {fmtMinute(summary.maxIdx)}
                </div>
              </div>
              <div>
                <div className="text-xs text-cyan-300">最低潮位</div>
                <div className="text-2xl font-mono text-cyan-200">
                  {summary.min.toFixed(2)}
                  <span className="text-sm text-slate-400 ml-0.5">m</span>
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {fmtMinute(summary.minIdx)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-300">潮差</div>
                <div className="text-2xl font-mono text-slate-200">
                  {summary.range.toFixed(2)}
                  <span className="text-sm text-slate-400 ml-0.5">m</span>
                </div>
              </div>
            </div>
            {windSummary.length > 0 && (
              <div className="mt-4 text-left">
                <div className="mb-2 text-xs text-slate-400 text-center">風の目安</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {windSummary.map((w) => (
                    <WindPeriod key={w.label} period={w} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

function CurrentWind({ wind }: { wind: WindForecastHour }): JSX.Element {
  const direction = formatWindDirection(wind.windDirectionDeg);
  const speedClass = windSpeedTextClass(wind.windSpeedMs);
  return (
    <div className="inline-flex items-center gap-2 rounded border border-slate-700/60 bg-slate-900/35 px-3 py-2">
      <span className="text-xs text-slate-400">現在の風</span>
      <span className={`text-xl leading-none ${speedClass}`}>{direction.arrow}</span>
      <span className="text-sm text-slate-100">{direction.compass}</span>
      <span className={`font-mono font-bold ${speedClass}`}>
        {wind.windSpeedMs.toFixed(1)}
        <span className="ml-1 text-xs text-slate-500">m/s</span>
      </span>
    </div>
  );
}

function WindPeriod({ period }: { period: WindPeriodSummary }): JSX.Element {
  const direction = formatWindDirection(period.windDirectionDeg);
  const speedClass = windSpeedTextClass(period.windSpeedMs);
  return (
    <div className="rounded border border-slate-700/50 bg-slate-900/30 px-2 py-2 text-center">
      <div className="text-[11px] text-slate-500">{period.label}</div>
      <div className="mt-1 flex items-center justify-center gap-1.5">
        <span className={`text-base leading-none ${speedClass}`}>{direction.arrow}</span>
        <span className="text-xs text-slate-200">{direction.compass}</span>
      </div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${speedClass}`}>
        {period.windSpeedMs.toFixed(1)}
        <span className="ml-0.5 text-[10px] text-slate-500">m/s</span>
      </div>
    </div>
  );
}

function fmtMinute(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
